import { useState, useEffect, useCallback, FC } from 'react';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { IconPlus, IconDeviceFloppy, IconX } from '@tabler/icons-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { DropdownPillComboboxSingleAdd } from 'ui-kit/Combobox/DropdownPillComboboxSingleAdd';
import { Pill, SortablePill } from 'ui-kit/Pill';

import { FetchResult, QueryResult, OperationVariables } from '@apollo/client';

import { GetPoolSettings, SavePoolSettings } from 'query/__generated__/poolManager';
import { IconButton } from '../../../../ui-kit';
import { KPI } from '../Dashboard/PoolManager/types';

type MetricsHeaderProps = {
  title: string;
  numPools?: number;
  metrics: KPI[];
  addUserSetting: (value: string) => Promise<FetchResult<SavePoolSettings>>;
  savedPoolSettings: QueryResult<GetPoolSettings, OperationVariables>;
  userPoolSettings?: string[];
};

export const MetricsHeader: FC<MetricsHeaderProps> = ({
  title,
  numPools,
  metrics,
  addUserSetting,
  savedPoolSettings,
  userPoolSettings,
}) => {
  const { loading, error } = savedPoolSettings;
  const [isEditMode, setIsEditMode] = useState(false);
  const [displayedMetrics, setDisplayedMetrics] = useState<KPI[]>([]);
  const [editDisplayedMetrics, setEditDisplayedMetrics] = useState<KPI[]>([]);
  const [menuMetrics, setMenuMetrics] = useState<KPI[]>([]);
  const [editMenuMetrics, setEditMenuMetrics] = useState<KPI[]>([]);
  // True from the moment Save is clicked until the mutation persisting the
  // new set resolves. The edit is applied to displayedMetrics immediately
  // (see handleSave) rather than waiting on the network round-trip — this
  // just dims the row in the meantime instead of freezing edit mode open
  // for a few seconds and then snapping to the new layout all at once.
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const saveUserSettings = useCallback(
    (metricsToUpdate: KPI[]) => {
      const selectorsToSave = metricsToUpdate.map((metric) => metric.selector);
      const formattedMetrics = JSON.stringify(selectorsToSave);
      return addUserSetting(formattedMetrics);
    },
    [addUserSetting],
  );

  useEffect(() => {
    if (!loading && !error) {
      if (!userPoolSettings || userPoolSettings.length === 0) {
        // If no metrics are saved, automatically add and save the first three
        const initialMetrics = metrics.slice(0, 3);
        setDisplayedMetrics(initialMetrics);
        setMenuMetrics(metrics.slice(3));
        saveUserSettings(initialMetrics);
      } else {
        // console.log('HERE', userPoolSettings);
        const accumulatedMetrics = userPoolSettings
          .map((selector) => metrics.find((metric) => metric.selector === selector))
          .filter(Boolean) as KPI[];
        setDisplayedMetrics(accumulatedMetrics);
        setMenuMetrics(
          metrics.filter(
            (metric) => !accumulatedMetrics.some((m) => m.selector === metric.selector),
          ),
        );
      }
    } else if (userPoolSettings == null) {
      setDisplayedMetrics([]);
      setMenuMetrics(metrics);
    }
  }, [userPoolSettings, metrics, loading, error, saveUserSettings]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setEditDisplayedMetrics((items) => {
        const oldIndex = items.findIndex((item) => item.selector === active.id);
        const newIndex = items.findIndex((item) => item.selector === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleRemove = useCallback(
    (selector?: string) => {
      if (selector && editDisplayedMetrics.length > 1) {
        setEditDisplayedMetrics((items) => {
          const removedItem = items.find((item) => item.selector === selector);
          if (removedItem) {
            setEditMenuMetrics((menuItems) => [...menuItems, removedItem]);
          }
          return items.filter((item) => item.selector !== selector);
        });
      }
    },
    [editDisplayedMetrics],
  );

  const handleAdd = useCallback(
    (selector?: string) => {
      if (selector) {
        const itemToAdd = editMenuMetrics.find((item) => item.selector === selector);
        if (itemToAdd) {
          setEditDisplayedMetrics((items) => [...items, itemToAdd]);
          setEditMenuMetrics((items) => items.filter((item) => item.selector !== selector));
        }
      }
    },
    [editMenuMetrics],
  );

  const handleEditMode = () => {
    setIsEditMode(true);
    setEditDisplayedMetrics([...displayedMetrics]);
    setEditMenuMetrics([...menuMetrics]);
  };

  const handleSave = () => {
    const metricsToSave = [...editDisplayedMetrics];
    const remainingMenu = [...editMenuMetrics];
    // Snapshot what's on screen *before* the optimistic update below, so a
    // failed mutation has something to roll back to.
    const previousDisplayedMetrics = displayedMetrics;
    const previousMenuMetrics = menuMetrics;

    // Commit optimistically and leave edit mode immediately — the mutation
    // below is just persisting what's already on screen, not producing it.
    setDisplayedMetrics(metricsToSave);
    setMenuMetrics(remainingMenu);
    setIsEditMode(false);
    setIsSaving(true);

    saveUserSettings(metricsToSave)
      .catch(() => {
        // The optimistic update above never actually persisted — revert it
        // rather than leaving metrics on screen that the backend doesn't
        // know about (until some unrelated refetch silently overwrites them
        // with the real, unsaved state).
        setDisplayedMetrics(previousDisplayedMetrics);
        setMenuMetrics(previousMenuMetrics);
        toast.error('Failed to save metrics — reverted to your previous selection.');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleCancel = () => {
    setEditDisplayedMetrics([...displayedMetrics]);
    setEditMenuMetrics([...menuMetrics]);
    setIsEditMode(false);
  };

  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="mb-3 flex items-start">
      <div className="w-48 flex-shrink-0">
        <div className="flex flex-col">
          <span className="truncate text-xs text-slate-400" title={title}>
            {title}
          </span>
          {numPools && <span className="text-xs text-white">{`${numPools} Pools`}</span>}
        </div>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToHorizontalAxis]}
      >
        <div
          className={clsx('flex flex-row flex-wrap items-center gap-2', {
            'pointer-events-none animate-pulse opacity-50': isSaving,
          })}
          aria-busy={isSaving || undefined}
        >
          {/* useUserSettings' addUserSetting mutation refetches
              GetPoolSettings on completion (see its onCompleted), which
              flips `loading` true again right as isSaving's optimistic
              update lands. Gating the skeleton on displayedMetrics being
              empty (real "nothing to show yet" — first load, or an
              asset-class switch that hasn't resolved) rather than on
              `loading` alone means that refetch doesn't blow away the
              pills we just optimistically set with a generic 4-pill
              skeleton; the isSaving dim above already communicates it. */}
          {loading && displayedMetrics.length === 0 ? (
            <div className="flex flex-row gap-5">
              {Array.from({ length: 4 }).map((_, index) => (
                <Pill isLoading key={index} />
              ))}
            </div>
          ) : (
            <SortableContext
              items={(isEditMode ? editDisplayedMetrics : displayedMetrics).map(
                (metric) => metric.selector,
              )}
              strategy={horizontalListSortingStrategy}
            >
              {(isEditMode ? editDisplayedMetrics : displayedMetrics).map((metric, _index) =>
                isEditMode ? (
                  <SortablePill
                    key={metric.selector}
                    id={metric.selector}
                    label={metric.description}
                    value={metric.value}
                    isDraggable={editDisplayedMetrics.length > 1}
                    isRemovable={editDisplayedMetrics.length > 1}
                    onRemove={() => handleRemove(metric.selector)}
                  />
                ) : (
                  <Pill key={metric.selector} label={metric.description} value={metric.value} />
                ),
              )}
            </SortableContext>
          )}
        </div>
      </DndContext>
      {isEditMode && (
        <div className="ml-4">
          <DropdownPillComboboxSingleAdd<KPI>
            options={editMenuMetrics}
            buttonLabel="Add more metrics"
            comboLabel="Add Metrics"
            manuLabel="All metrics"
            inputPlaceholder="Search metrics…"
            // PillButtonCombobox's option row only has room for one label —
            // fold the metric's live value into it (e.g. "WAM 293") so
            // picking a metric to add doesn't lose the preview it used to
            // show as Pill's separate `value` slot.
            getOptionLabel={(metric) => `${metric.description} ${metric.value}`}
            getOptionValue={(metric) => metric.selector}
            onValueSelected={(metric) => handleAdd(metric.selector)}
          />
        </div>
      )}
      <div className="ml-8 flex gap-2">
        {/* Visibility follows the same "is there data to show" rule as the
            pills' skeleton above, not raw `loading` — the post-save refetch
            flips `loading` true again well after isSaving has already gone
            back to false (both fire off the same mutation-resolve tick, so
            there's no window where isSaving alone would still be true),
            and displayedMetrics isn't cleared out from under a refetch of
            already-loaded data. Gating on `!loading` alone would make the
            button disappear entirely for that window instead of just
            staying disabled through it. */}
        {!isEditMode && (displayedMetrics.length > 0 || !loading) && (
          <IconButton
            tooltipText={isSaving || loading ? 'Saving…' : 'Edit metrics'}
            ariaLabel="Edit metrics"
            onClick={handleEditMode}
            disabled={isSaving || loading}
            className="h-8 w-8 rounded-full border border-gray-700 bg-gray-950  p-[7px] hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-pink-400"
            variant="icon-only"
            icon={<IconPlus size={16} className="text-gray-200" />}
          />
        )}
        {isEditMode && (
          <>
            <IconButton
              tooltipText="Save changes"
              ariaLabel="Save changes"
              onClick={handleSave}
              className=" h-8 w-8 rounded-full border border-gray-700 bg-gray-950 p-[7px] hover:bg-gray-900
            focus:outline-none"
              variant="icon-only"
              icon={<IconDeviceFloppy size={16} className="text-pink-400" />}
            />
            <IconButton
              tooltipText="Cancel editing"
              ariaLabel="Cancel editing"
              onClick={handleCancel}
              className="h-8 w-8 rounded-full border border-gray-700 bg-gray-950  p-[7px] hover:bg-gray-900 focus:outline-none"
              variant="icon-only"
              icon={<IconX size={16} className="text-gray-200" />}
            />
          </>
        )}
      </div>
    </div>
  );
};
